import React, { useMemo, useState } from "react";
import { Loader2, X, Package, Truck, MapPin, Users, School, ImagePlus, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { publishListingUpdate } from "@/lib/listingBus";
import { compressImage } from "@/lib/imageCompress";
import ConditionPicker from "@/components/ConditionPicker";
import ClothingPickers from "@/components/ClothingPickers";

/**
 * EditListingModal — canonical seller-side listing editor.
 *
 * Reused across every seller surface (Parent Dashboard moderation queue,
 * Parent Ledger Live Listings, Kid Profile listings grid, and the
 * Individual Listing Detail page). Persists the full seller-editable
 * field set via `PATCH /api/listings/{id}` — including the
 * web-tier-only `parent_fulfillment_choices` and `parent_posted` fields
 * that live in the metadata shim.
 *
 * Props:
 *   listing:  the current listing payload (id required)
 *   onClose:  () => void
 *   onSaved:  (updatedListing) => void   // called with the merged listing
 */

const CATEGORIES = [
  { value: "baby_toddler", label: "Baby-Toddler",     ageHint: "0-5 yrs" },
  { value: "junior_hub",   label: "Tiny Traders",     ageHint: "6-10 yrs" },
  { value: "collectors",   label: "Tween Collectors", ageHint: "11-15 yrs" },
  { value: "teen",         label: "Teens",            ageHint: "16+ yrs" },
];

const PARCEL_SIZES = [
  { value: "small",  label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large",  label: "Large" },
  { value: "bulky",  label: "Bulky Item" },
];

// Iteration 208 — 4 canonical selectable fulfillment modes.
// `home_delivery` = Royal Mail Post (internal key preserved for upstream
// contract). `collection_only` and `delivery_only` are NOT here — they
// live below as Parent Handoff sub-badges.
// Iteration 263 — `local_shop` is permanently unavailable (Coming Soon);
// it renders disabled and is stripped from saved choices.
const FULFILLMENT_CHOICES = [
  { value: "parent_handoff",  label: "Parent Handoff",             icon: Users  },
  { value: "school_gate",     label: "At/After School Collection", icon: School },
  { value: "local_shop",      label: "Local Shop Pick-Up",         icon: MapPin, disabled: true },
  { value: "home_delivery",   label: "Royal Mail Post",            icon: Truck  },
];

// Iteration 208 — Parent Handoff visual sub-badges. NOT selectable
// fulfillment modes on their own. Rendered on the public listing card
// so buyers see the seller's preferred handoff method at a glance.
const PARENT_HANDOFF_BADGES = [
  { value: "collection_only", label: "Collection Only", sub: "Buyer collects from seller"  },
  { value: "delivery_only",   label: "Delivery Only",   sub: "Seller drops off at buyer"   },
];

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block mb-3">
      <span className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">{label}</span>
      {children}
    </label>
  );
}

export default function EditListingModal({ listing, onClose, onSaved, adminKey }) {
  const [title, setTitle] = useState(listing.title || "");
  const [price, setPrice] = useState(String(listing.price ?? ""));
  const [description, setDescription] = useState(listing.description || "");
  const [condition, setCondition] = useState(listing.condition || "used_good");
  // Iteration 124 — multi-select age categories (matches Kid Zone form).
  // First pick = primary category; rest go into secondary_categories.
  const [categories, setCategories] = useState(() => {
    const primary = listing.category || "junior_hub";
    const secondary = Array.isArray(listing.secondary_categories)
      ? listing.secondary_categories
      : [];
    const merged = [primary, ...secondary.filter((c) => c !== primary)];
    return merged.slice(0, 3);
  });
  const [packageSize, setPackageSize] = useState(listing.package_size || "small");
  // Iteration 301 — clothing sub-category attributes (shared chip panel,
  // same as Kid Zone + /sell). Editable whenever Clothing is among the
  // picked categories.
  const [clothing, setClothing] = useState({
    clothing_subcategory: listing.clothing_subcategory || "",
    clothing_gender: listing.clothing_gender || "",
    clothing_size: listing.clothing_size || "",
    clothing_brand: listing.clothing_brand || "",
  });
  const clothingPicked = categories.includes("clothing");
  // Iteration 174 — parcel weight override (grams). Empty string means
  // "use the platform default" (500g).
  const [weightGrams, setWeightGrams] = useState(
    listing.weight_grams != null ? String(listing.weight_grams) : "",
  );
  const [parentPosted, setParentPosted] = useState(!!listing.parent_posted);
  const [shippingAvailable, setShippingAvailable] = useState(!!listing.shipping_available);
  const [fulfillmentChoices, setFulfillmentChoices] = useState(() => {
    const existing = Array.isArray(listing.parent_fulfillment_choices)
      ? listing.parent_fulfillment_choices
      : [];
    // Iteration 208 — `home_delivery` (Royal Mail Post) mirrors
    // `shipping_available`. Historic listings may still carry
    // `school_gate` as the Royal Mail marker; migrate on load.
    const migrated = existing.map((c) => (c === "school_gate" && listing.shipping_available && !existing.includes("home_delivery") ? "home_delivery" : c));
    if (listing.shipping_available && !migrated.includes("home_delivery")) {
      return [...migrated, "home_delivery"];
    }
    return migrated;
  });
  // Iteration 124 — full photo replacement. Photos stored as data-URLs so
  // both existing (upstream) URLs and freshly-uploaded base64 payloads
  // coexist in the same list. Only pushed to backend when the parent
  // explicitly changes the set (initial photos are preserved by
  // omitting them from the PATCH payload).
  const [photos, setPhotos] = useState(() =>
    Array.isArray(listing.photos) ? listing.photos.filter(Boolean).slice(0, 5) : [],
  );
  const [photosDirty, setPhotosDirty] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoErr, setPhotoErr] = useState("");

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const toggleCategory = (value) => {
    setCategories((prev) => {
      if (prev.includes(value)) {
        // Must keep at least one — snap to junior_hub default.
        const next = prev.filter((v) => v !== value);
        return next.length ? next : ["junior_hub"];
      }
      if (prev.length >= 3) return prev; // enforce max 3
      return [...prev, value];
    });
  };

  const addPhoto = async (file) => {
    if (!file) return;
    if (photos.length >= 5) {
      setPhotoErr("Max 5 photos per listing.");
      return;
    }
    setPhotoErr("");
    setPhotoUploading(true);
    try {
      const compressed = await compressImage(file);
      setPhotos((p) => [...p, compressed]);
      setPhotosDirty(true);
    } catch (e2) {
      setPhotoErr("Couldn't process that photo — try another.");
    } finally {
      setPhotoUploading(false);
    }
  };

  const removePhoto = (idx) => {
    setPhotos((p) => p.filter((_, i) => i !== idx));
    setPhotosDirty(true);
  };

  const toggleChoice = (value) => {
    setFulfillmentChoices((prev) => {
      const next = prev.includes(value)
        ? prev.filter((c) => c !== value)
        : [...prev, value];
      // Iteration 208 — `home_delivery` (Royal Mail Post) is the
      // sole checkout mode that flips the `shipping_available` flag.
      if (value === "home_delivery") {
        setShippingAvailable(next.includes("home_delivery"));
      }
      // Iteration 208 — clearing Parent Handoff also clears its
      // sub-badges (Collection Only / Delivery Only) so read paths
      // never render orphaned badges.
      if (value === "parent_handoff" && !next.includes("parent_handoff")) {
        return next.filter((c) => c !== "collection_only" && c !== "delivery_only");
      }
      return next;
    });
  };

  const canSave = useMemo(() => {
    if (!title.trim()) return false;
    const p = parseFloat(price);
    if (!Number.isFinite(p) || p < 0) return false;
    return true;
  }, [title, price]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!canSave) { setErr("Enter a valid title and price."); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        description,
        price: parseFloat(price),
        condition,
        // Iteration 124 — multi-select age categories.
        category: categories[0],
        secondary_categories: categories.slice(1, 3),
        package_size: packageSize,
        parent_posted: parentPosted,
        shipping_available: shippingAvailable,
        // Iteration 208 — `home_delivery` is Royal Mail Post; the
        // `shipping_available` flag is the source of truth for that
        // mode. Strip it from `parent_fulfillment_choices` so only
        // *local* seller-picked options land in the shim.
        // Iteration 263 — also strip `local_shop`: permanently
        // unavailable platform-wide (Coming Soon).
        parent_fulfillment_choices: fulfillmentChoices.filter((c) => c !== "home_delivery" && c !== "local_shop"),
      };
      // Iteration 174 — weight override only sent when the parent
      // actually types a value (blank → keep backend default).
      if (weightGrams && Number(weightGrams) > 0) {
        payload.weight_grams = Number(weightGrams);
      }
      // Iteration 301 — clothing attributes. Sent whenever Clothing is
      // among the picked categories (empty strings clear prior picks);
      // when Clothing leaves the picks entirely and the listing HAD
      // clothing attrs, all four are sent empty so the backend scrubs
      // the stale values.
      if (clothingPicked) {
        for (const k of ["clothing_subcategory", "clothing_gender", "clothing_size", "clothing_brand"]) {
          payload[k] = clothing[k] || "";
        }
      } else if (listing.clothing_subcategory || listing.clothing_gender || listing.clothing_size || listing.clothing_brand) {
        payload.clothing_subcategory = "";
        payload.clothing_gender = "";
        payload.clothing_size = "";
        payload.clothing_brand = "";
      }
      // Only push photos when the user actually touched them — avoids
      // resubmitting the whole gallery (and re-triggering AI moderation)
      // on every field save.
      if (photosDirty) {
        payload.photos = photos;
      }
      // Iteration 160 — Platform Admin full edit mode. When `adminKey`
      // is passed we hit the admin variant of the edit endpoint which
      // uses the ADMIN_API_KEY as the upstream bearer and tags
      // `edited_by_admin=true` for audit.
      const endpoint = adminKey ? `/admin/listings/${listing.id}` : `/listings/${listing.id}`;
      const options = adminKey ? { headers: { "X-Admin-Key": adminKey } } : undefined;
      const { data } = await api.patch(endpoint, payload, options);
      publishListingUpdate(data || { id: listing.id, ...payload });
      onSaved?.(data);
    } catch (e2) {
      setErr(formatApiError(e2) || "Couldn't save your changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4 py-6 overflow-y-auto" data-testid="edit-listing-modal">
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl font-medium text-slate-900">
              {adminKey ? "Admin — Full Edit" : "Edit listing"}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {adminKey
                ? "Support override — changes are stamped edited_by_admin in the audit log."
                : "Changes sync everywhere the listing appears — Browse, Search, Dashboards and Listing Detail."}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="edit-listing-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Field label="Title" htmlFor="edit-title">
            <input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none"
              data-testid="edit-listing-title"
            />
          </Field>
          <Field label="Price (£)" htmlFor="edit-price">
            <input
              id="edit-price"
              type="number" step="0.01" min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none"
              data-testid="edit-listing-price"
            />
          </Field>
        </div>

        <Field label="Description" htmlFor="edit-desc">
          <textarea
            id="edit-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none resize-none"
            data-testid="edit-listing-description"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Field label="Condition" htmlFor="edit-condition">
            <div id="edit-condition">
              <ConditionPicker
                value={condition}
                onChange={setCondition}
                testIdPrefix="edit-listing-condition"
              />
            </div>
          </Field>
          <Field label="Parcel Size" htmlFor="edit-parcel">
            <select
              id="edit-parcel"
              value={packageSize}
              onChange={(e) => setPackageSize(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white"
              data-testid="edit-listing-package-size"
            >
              {PARCEL_SIZES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* Iteration 174 — parcel weight override (grams) for Royal Mail Click & Drop. */}
        <div className="mb-3">
          <Field label="Parcel weight (grams)" htmlFor="edit-weight">
            <input
              id="edit-weight"
              type="number"
              min="1"
              max="30000"
              placeholder="500"
              value={weightGrams}
              onChange={(e) => setWeightGrams(e.target.value.replace(/[^0-9]/g, ""))}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white"
              data-testid="edit-listing-weight-grams"
            />
            <p className="mt-1 text-[11px] text-slate-500">Optional. Defaults to 500g when blank — used to pick the Royal Mail service tier when a label is generated.</p>
          </Field>
        </div>

        {/* Iteration 124 — multi-select age categories (matches Kid Zone). */}
        <div className="mb-3" data-testid="edit-listing-categories-wrap">
          <div className="flex items-center justify-between mb-1">
            <span className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
              Age categories <span className="text-slate-400 font-normal">· pick up to 3</span>
            </span>
            <span className="text-[11px] text-slate-500" data-testid="edit-listing-categories-count">
              {categories.length}/3
            </span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="edit-listing-category-chips">
            {CATEGORIES.map((c) => {
              const picked = categories.includes(c.value);
              const atMax = !picked && categories.length >= 3;
              return (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => toggleCategory(c.value)}
                  disabled={atMax}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition disabled:opacity-40 disabled:cursor-not-allowed ${picked ? "bg-brandTeal text-white border-brandTeal" : "bg-white text-slate-700 border-slate-200 hover:border-brandTeal"}`}
                  data-testid={`edit-listing-category-chip-${c.value}`}
                  aria-pressed={picked}
                >
                  {c.label}{c.ageHint ? ` · ${c.ageHint}` : ""}
                </button>
              );
            })}
          </div>
        </div>

        {/* Iteration 301 — clothing sub-category pickers (shared chip
            panel; exact parity with Kid Zone + /sell). */}
        {clothingPicked && (
          <div className="mb-3" data-testid="edit-listing-clothing-wrap">
            <ClothingPickers
              testIdPrefix="edit-listing-clothing"
              values={clothing}
              onChange={(updates) => setClothing((c) => ({ ...c, ...updates }))}
            />
          </div>
        )}

        {/* Iteration 124 — Photos gallery editor (up to 5). */}
        <div className="mb-3" data-testid="edit-listing-photos-wrap">
          <div className="flex items-center justify-between mb-1">
            <span className="block text-xs font-bold text-slate-600 uppercase tracking-wider">
              Photos <span className="text-slate-400 font-normal">· up to 5</span>
            </span>
            <span className="text-[11px] text-slate-500" data-testid="edit-listing-photos-count">
              {photos.length}/5
            </span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="edit-listing-photos-grid">
            {photos.map((src, idx) => (
              <div key={`${idx}-${src?.slice(-16)}`} className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-200 group" data-testid={`edit-listing-photo-${idx}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-600 text-white shadow-md opacity-0 group-hover:opacity-100 transition"
                  aria-label={`Remove photo ${idx + 1}`}
                  data-testid={`edit-listing-photo-remove-${idx}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {photos.length < 5 && (
              <label
                className={`w-20 h-20 rounded-xl border-2 border-dashed border-slate-300 hover:border-brandTeal inline-flex items-center justify-center text-slate-400 hover:text-brandTeal transition cursor-pointer ${photoUploading ? "opacity-60 pointer-events-none" : ""}`}
                data-testid="edit-listing-photo-add"
              >
                {photoUploading
                  ? <Loader2 className="w-5 h-5 animate-spin" />
                  : <ImagePlus className="w-5 h-5" />}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => addPhoto(e.target.files?.[0])}
                  data-testid="edit-listing-photo-input"
                />
              </label>
            )}
          </div>
          {photoErr && <div className="text-xs text-rose-700 mt-1" data-testid="edit-listing-photo-error">{photoErr}</div>}
        </div>

        <Field label="Fulfillment options" htmlFor="edit-fulfillment">
          <div id="edit-fulfillment" className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="edit-listing-fulfillment">
            {FULFILLMENT_CHOICES.map((c) => {
              const Icon = c.icon;
              const on = fulfillmentChoices.includes(c.value);
              return (
                <button
                  type="button"
                  key={c.value}
                  onClick={() => !c.disabled && toggleChoice(c.value)}
                  disabled={!!c.disabled}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-2xl border text-sm font-semibold transition text-left ${
                    c.disabled
                      ? "bg-slate-50 border-slate-200 text-slate-400 opacity-60 cursor-not-allowed"
                      : on
                        ? "bg-brandTeal/10 border-brandTeal text-brandTealDark"
                        : "bg-white border-slate-200 text-slate-700 hover:border-brandSlate"
                  }`}
                  data-testid={`edit-listing-fulfillment-${c.value}`}
                  aria-pressed={on}
                >
                  <Icon className="w-4 h-4" /> {c.label}
                  {c.disabled && (
                    <span className="ml-auto pill bg-brandSlate/10 text-brandSlate !text-[10px] !py-0.5 !px-2" data-testid="edit-listing-fulfillment-local-shop-coming-soon">Coming soon</span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Pick every option that works for this item.</p>
          {/* Iteration 208 — Parent Handoff sub-badges. Visible only
              when Parent Handoff is enabled. These are visual tags,
              not selectable checkout modes. */}
          {fulfillmentChoices.includes("parent_handoff") && (
            <div
              className="mt-3 rounded-2xl border border-amber-200 bg-amber-50/50 p-3"
              data-testid="edit-listing-handoff-badges"
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-2">
                Parent Handoff details (shown as tags on the listing)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PARENT_HANDOFF_BADGES.map((b) => {
                  const on = fulfillmentChoices.includes(b.value);
                  return (
                    <button
                      type="button"
                      key={b.value}
                      onClick={() => toggleChoice(b.value)}
                      className={`text-left px-3 py-2 rounded-xl border text-xs font-semibold transition ${
                        on
                          ? "bg-amber-100 border-amber-400 text-amber-900"
                          : "bg-white border-slate-200 text-slate-700 hover:border-amber-300"
                      }`}
                      data-testid={`edit-listing-handoff-badge-${b.value}`}
                      aria-pressed={on}
                    >
                      <div className="font-bold">{b.label}</div>
                      <div className="text-[10px] font-medium text-slate-500 mt-0.5">{b.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </Field>

        <div className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3 mb-4" data-testid="edit-listing-parent-posted-row">
          <div>
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-violet-600" /> Parent Posted
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Flags this listing as posted by a parent on behalf of a child.</p>
          </div>
          <button
            type="button"
            onClick={() => setParentPosted((v) => !v)}
            role="switch"
            aria-checked={parentPosted}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 transition-colors ${
              parentPosted ? "bg-violet-600 border-violet-600" : "bg-slate-200 border-slate-200"
            }`}
            data-testid="edit-listing-parent-posted-toggle"
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                parentPosted ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {err && <div className="text-rose-700 text-sm mb-3" data-testid="edit-listing-error">{err}</div>}

        <div className="flex items-center gap-2 justify-end pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-bold text-slate-700 hover:bg-slate-100"
            data-testid="edit-listing-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !canSave}
            className="px-5 py-2 rounded-full text-sm font-bold bg-brandSlate text-white hover:bg-brandSlateDark disabled:opacity-60"
            data-testid="edit-listing-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
